/**
 * AuthWizardLayout
 * 
 * Split-screen wizard layout for authentication and onboarding flows.
 * - Left: Marketing content with logo and feature highlights
 * - Right: Step content (forms, confirmations, etc.)
 * 
 * Implements ThreadLock brand styling (navy #1b3a4d, orange #fb7a1e).
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Scale, Users, Clock } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';

interface AuthWizardLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
}

export function AuthWizardLayout({ children, title, subtitle }: AuthWizardLayoutProps) {
  return (
    <div className="flex h-screen bg-[#0B1724]">
      {/* Left Panel: Marketing Content */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#0B1724] via-[#0d2433] to-[#0B1724] text-white p-12 flex-col justify-between">
        <div>
          {/* Logo */}
          <BrandLogo className="text-2xl" />
        </div>

        <div className="space-y-8">
          {/* Headline */}
          <div>
            <h1 className="text-4xl font-bold mb-4">
              Let's set up your case workspace
            </h1>
            <p className="text-lg text-slate-300">
              We'll ask a few basic questions so we can organize your case correctly.
            </p>
            <p className="text-lg text-slate-300">
              This takes about a minute.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-sm text-slate-400">
          &copy; {new Date().getFullYear()} ThreadLock. All rights reserved.
        </div>
      </div>

      {/* Right Panel: Step Content */}
      <div className="flex-1 flex flex-col bg-[#0B1724] overflow-y-auto">
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md space-y-6">
            {/* Optional Title/Subtitle */}
            {title && (
              <div className="text-center">
                <h2 className="text-3xl font-bold text-white">{title}</h2>
                {subtitle && <p className="mt-2 text-sm text-slate-300">{subtitle}</p>}
              </div>
            )}

            {/* Step Content */}
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

interface FeatureItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

function FeatureItem({ icon, title, description }: FeatureItemProps) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 text-[#fb7a1e]">{icon}</div>
      <div>
        <h3 className="font-semibold mb-1">{title}</h3>
        <p className="text-sm text-slate-300">{description}</p>
      </div>
    </div>
  );
}
